import { Task, Update, child, defModule, type TaskMessage, type UpdateResult } from '@st/redux'
import type { OrganizationMembership, OrganizationRole } from '@st/sdk'
import { Effect } from 'effect'
import { match, P } from '@st/pattern'
import { Routing, SdkClient } from '~/effects'
import { observeCurrentUser, observeRoute, runSdkRequest } from '~/features/module-runtime'
import {
  organizationSignUpModule,
  organizationSignUpOwnsAuthScreen,
  type OrganizationSignUpMessage,
  type OrganizationSignUpState
} from '../organizations/organization-sign-up-module'

export type ConnectApp = {
  clientId: string
  name: string
  permissions: ReadonlyArray<string>
  logo?: string
  autoAuthorize?: boolean
  allowedOrganizationRoles: OrganizationRole[]
}

const organizationDataPermissions = [
  'Client organizer data',
  'Client document uploads',
  'Preparer workpapers',
  'Notifications for document uploads and client organizer submissions'
] as const

const connectApps: ReadonlyArray<ConnectApp> = [
  {
    clientId: 'karbon-production',
    name: 'Karbon',
    logo: '/icons/karbon-logo.png',
    allowedOrganizationRoles: ['owner'],
    permissions: organizationDataPermissions
  },
  {
    clientId: 'karbon-staging',
    name: 'Karbon',
    logo: '/icons/karbon-logo.png',
    allowedOrganizationRoles: ['owner'],
    permissions: organizationDataPermissions
  },
  {
    clientId: 'thinkific-client',
    name: 'Thinkific',
    allowedOrganizationRoles: ['owner', 'member', 'restricted'],
    permissions: [],
    autoAuthorize: true
  },
  {
    clientId: 'stanfordtax-agent',
    name: 'StanfordTax Desktop Agent',
    allowedOrganizationRoles: ['owner'],
    permissions: organizationDataPermissions
  }
]

const observeConnectRoute = observeRoute({ name: 'connect_app' })
type ObservedConnectRoute = TaskMessage<typeof observeConnectRoute>['route']

type ConnectRequest = {
  clientId: string
  oauthState: string
  redirectUri: string
  scope: string
  codeChallenge?: string
  codeChallengeMethod?: string
}

type UnavailableConnectRoute =
  | { status: 'inactive' }
  | { status: 'invalid'; reason: 'missing_params' | 'unknown_app'; clientId?: string }

type ActiveConnectRoute = {
  status: 'active'
  app: ConnectApp
  request: ConnectRequest
}

type ConnectRoute = UnavailableConnectRoute | ActiveConnectRoute

type ConnectAppContext = {
  authPage: 'signUp' | 'login'
  signUp: OrganizationSignUpState
}

type AuthorizationState = { status: 'idle' } | { status: 'authorizing' } | { status: 'failed' }

type AuthorizationOwner = {
  userId: string
  route: ActiveConnectRoute
  memberships: OrganizationMembership[]
  selectedOrganizationId: string | null
}

export type ConnectAppModuleState = ConnectAppContext &
  (
    | { status: 'waitingForAuth'; route: ConnectRoute }
    | { status: 'loggedOut'; route: ConnectRoute }
    | { status: 'signingUp'; userId: string; route: ConnectRoute }
    | { status: 'routeUnavailable'; userId: string; route: UnavailableConnectRoute }
    | { status: 'loadingMemberships'; userId: string; route: ActiveConnectRoute }
    | { status: 'membershipsFailed'; userId: string; route: ActiveConnectRoute }
    | {
        status: 'ready'
        userId: string
        route: ActiveConnectRoute
        memberships: OrganizationMembership[]
        selectedOrganizationId: string | null
        authorization: AuthorizationState
      }
    | {
        status: 'finishing'
        route: ConnectRoute
        currentUserId: string | null
        owner: AuthorizationOwner
      }
  )

export type AuthenticatedConnectAppState = Exclude<
  ConnectAppModuleState,
  { status: 'waitingForAuth' | 'loggedOut' | 'signingUp' | 'finishing' }
>

const signUp = child(organizationSignUpModule, 'signUp')

const loadMemberships = Task.define((_userId: string) =>
  Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* runSdkRequest(
      sdk.send({ type: 'organizations/getMyOrganizationMemberships' })
    )
    if (!result.ok) return { type: 'membershipsFailed' } as const
    return { type: 'membershipsLoaded', memberships: result.response.memberships } as const
  })
)

export type ConnectAppMessage =
  | TaskMessage<typeof observeConnectRoute>
  | TaskMessage<typeof observeCurrentUser>
  | TaskMessage<typeof loadMemberships>
  | { type: 'signUp'; message: OrganizationSignUpMessage }
  | { type: 'showLogin' }
  | { type: 'showSignUp' }
  | { type: 'reloadMemberships' }
  | { type: 'selectOrganization'; organizationId: string }
  | { type: 'authorizeClicked' }
  | { type: 'authorizationCompleted' }
  | { type: 'authorizationFailed' }

/**
 * Connect-app topology:
 *
 * waitingForAuth --loggedOut--> loggedOut
 *       |
 *    loggedIn
 *       +-- inactive/invalid route --> routeUnavailable --valid route--+
 *       +-- active route ----------------------------------------------+--> loadingMemberships
 *
 * loggedOut --embedded sign-up authenticates--> signingUp
 *                                                  | organization created
 *                                                  +---------------------> loadingMemberships
 *                                                                          | loaded
 *                           membershipsFailed <--failed--+                  v
 *                                  +--------retry---------+               ready
 *                                                                         | authorize
 *                                                                         v
 *                                                ready(failed) <--failed-- authorizing
 *                                                                         | authorized + redirected
 *                                                                         v
 *                                                                     ready(idle)
 *
 * If route or auth context is replaced after authorization is accepted, the
 * visible state becomes `finishing`. It owns the same Command until it settles,
 * then resumes the latest observed context.
 *
 * Sign-up is a scoped child: its state, Tasks, and messages belong to this Connect instance rather
 * than the standalone sign-up page. The parent waits in `signingUp` when Auth changes mid-flow and
 * enters its authenticated route only after the child finishes organization creation.
 *
 * Route, auth, and membership loading are state-owned Tasks. Authorization plus
 * redirect is one transition-owned Command, because accepting the authorization
 * must make it durable across later state changes. Apps marked `autoAuthorize`
 * start that same Command as soon as an eligible membership is loaded.
 */
export const connectAppModule = defModule<{
  state: ConnectAppModuleState
  message: ConnectAppMessage
}>({
  init: () =>
    Update.mapState(signUp.init(undefined), (signUp) => ({
      status: 'waitingForAuth',
      route: { status: 'inactive' },
      authPage: 'signUp',
      signUp
    })),
  tasks: connectAppTasks,
  update: updateConnectApp
})

function connectAppTasks(state: ConnectAppModuleState) {
  const observations = [observeConnectRoute(), observeCurrentUser(), ...signUp.tasks(state.signUp)]
  if (state.status == 'loadingMemberships') {
    return [...observations, loadMemberships(state.userId)]
  }
  return observations
}

function updateConnectApp(
  state: ConnectAppModuleState,
  message: ConnectAppMessage
): UpdateResult<ConnectAppModuleState, ConnectAppMessage> {
  return match({ state, message })
    .returnType<UpdateResult<ConnectAppModuleState, ConnectAppMessage>>()
    .with({ message: { type: 'routeChanged' } }, ({ state, message }) =>
      updateRoute(state, parseConnectRoute(message.route))
    )
    .with({ message: { type: 'currentUserChanged' } }, ({ state, message }) =>
      updateCurrentUser(state, message.currentUserId)
    )
    .with({ message: { type: 'signUp' } }, ({ state, message }) =>
      Update.pipe(
        signUp.update(state.signUp, message.message, (signUp) => ({ ...state, signUp })),
        reconcileCompletedSignUp
      )
    )
    .with({ message: { type: 'showLogin' } }, ({ state }) => ({
      ...state,
      authPage: 'login'
    }))
    .with({ message: { type: 'showSignUp' } }, ({ state }) => ({
      ...state,
      authPage: 'signUp'
    }))
    .with(
      { state: { status: 'loadingMemberships' }, message: { type: 'membershipsLoaded' } },
      ({ state, message }) => finishLoadingMemberships(state, message.memberships)
    )
    .with(
      { state: { status: 'loadingMemberships' }, message: { type: 'membershipsFailed' } },
      ({ state }) => ({ ...state, status: 'membershipsFailed' })
    )
    .with(
      { state: { status: 'membershipsFailed' }, message: { type: 'reloadMemberships' } },
      ({ state }) => ({ ...state, status: 'loadingMemberships' })
    )
    .with(
      {
        state: { status: 'ready', authorization: { status: P.union('idle', 'failed') } },
        message: { type: 'selectOrganization' }
      },
      ({ state, message }) => selectOrganization(state, message.organizationId)
    )
    .with(
      {
        state: { status: 'ready', authorization: { status: P.union('idle', 'failed') } },
        message: { type: 'authorizeClicked' }
      },
      ({ state }) => startAuthorization(state)
    )
    .with(
      {
        state: { status: 'ready', authorization: { status: 'authorizing' } },
        message: { type: 'authorizationCompleted' }
      },
      ({ state }) => ({ ...state, authorization: { status: 'idle' } })
    )
    .with(
      {
        state: { status: 'ready', authorization: { status: 'authorizing' } },
        message: { type: 'authorizationFailed' }
      },
      ({ state }) => ({ ...state, authorization: { status: 'failed' } })
    )
    .with(
      {
        state: { status: 'finishing' },
        message: { type: 'authorizationCompleted' }
      },
      ({ state }) => resumeLatestContext(state, 'idle')
    )
    .with(
      {
        state: { status: 'finishing' },
        message: { type: 'authorizationFailed' }
      },
      ({ state }) => resumeLatestContext(state, 'failed')
    )
    .otherwise(({ state, message }) => unexpectedMessage(state, message))
}

function parseConnectRoute(route: ObservedConnectRoute): ConnectRoute {
  if (!route) {
    return { status: 'inactive' }
  }

  if (!route.client_id || !route.state || !route.redirect_uri) {
    return { status: 'invalid', reason: 'missing_params', clientId: route.client_id }
  }

  const app = connectApps.find((candidate) => candidate.clientId == route.client_id)
  if (!app) {
    return { status: 'invalid', reason: 'unknown_app', clientId: route.client_id }
  }

  return {
    status: 'active',
    app,
    request: {
      clientId: route.client_id,
      oauthState: route.state,
      redirectUri: route.redirect_uri,
      scope: route.scope ?? '',
      codeChallenge: route.code_challenge,
      codeChallengeMethod: route.code_challenge_method
    }
  }
}

function updateRoute(
  state: ConnectAppModuleState,
  route: ConnectRoute
): UpdateResult<ConnectAppModuleState, ConnectAppMessage> {
  if (sameConnectRoute(state.route, route)) {
    return state
  }

  const routeChangedState = { ...state, authPage: 'signUp' as const }

  return match(routeChangedState)
    .returnType<UpdateResult<ConnectAppModuleState, ConnectAppMessage>>()
    .with({ status: P.union('waitingForAuth', 'loggedOut', 'signingUp') }, (state) => ({
      ...state,
      route
    }))
    .with({ status: 'routeUnavailable' }, (state) =>
      enterAuthenticatedRoute(state, state.userId, route)
    )
    .with({ status: 'loadingMemberships' }, (state) => {
      if (route.status == 'active') {
        return { ...state, route }
      }
      return enterAuthenticatedRoute(state, state.userId, route)
    })
    .with({ status: 'membershipsFailed' }, (state) =>
      enterAuthenticatedRoute(state, state.userId, route)
    )
    .with({ status: 'ready' }, (state) => {
      if (state.authorization.status == 'authorizing') {
        return finishAuthorization(state, route, state.userId)
      }
      if (route.status != 'active') {
        return enterAuthenticatedRoute(state, state.userId, route)
      }
      return maybeAutoAuthorize({
        ...state,
        route,
        selectedOrganizationId: null,
        authorization: { status: 'idle' }
      })
    })
    .with({ status: 'finishing' }, (state) => ({ ...state, route }))
    .exhaustive()
}

function updateCurrentUser(
  state: ConnectAppModuleState,
  currentUserId: string | null
): UpdateResult<ConnectAppModuleState, ConnectAppMessage> {
  if (state.status == 'finishing') {
    return state.currentUserId == currentUserId ? state : { ...state, currentUserId }
  }

  if (
    state.status == 'ready' &&
    state.authorization.status == 'authorizing' &&
    state.userId != currentUserId
  ) {
    return finishAuthorization(state, state.route, currentUserId)
  }

  if (!currentUserId) {
    return {
      status: 'loggedOut',
      route: state.route,
      authPage: 'signUp',
      signUp: state.signUp
    }
  }

  if (organizationSignUpOwnsAuthScreen(state.signUp)) {
    return {
      status: 'signingUp',
      route: state.route,
      userId: currentUserId,
      authPage: state.authPage,
      signUp: state.signUp
    }
  }

  return updateLoggedIn(state, currentUserId)
}

function reconcileCompletedSignUp(state: ConnectAppModuleState): ConnectAppModuleState {
  if (state.status != 'signingUp' || organizationSignUpOwnsAuthScreen(state.signUp)) {
    return state
  }
  return enterAuthenticatedRoute(state, state.userId, state.route)
}

function updateLoggedIn(state: ConnectAppModuleState, userId: string): ConnectAppModuleState {
  if (
    state.status != 'waitingForAuth' &&
    state.status != 'loggedOut' &&
    state.status != 'signingUp' &&
    state.status != 'finishing' &&
    state.userId == userId
  ) {
    return state
  }

  return enterAuthenticatedRoute(state, userId, state.route)
}

function enterAuthenticatedRoute(
  context: ConnectAppContext,
  userId: string,
  route: ConnectRoute
): ConnectAppModuleState {
  if (route.status == 'active') {
    return {
      status: 'loadingMemberships',
      route,
      userId,
      authPage: context.authPage,
      signUp: context.signUp
    }
  }

  return {
    status: 'routeUnavailable',
    route,
    userId,
    authPage: context.authPage,
    signUp: context.signUp
  }
}

function finishLoadingMemberships(
  state: Extract<ConnectAppModuleState, { status: 'loadingMemberships' }>,
  memberships: OrganizationMembership[]
): UpdateResult<ConnectAppModuleState, ConnectAppMessage> {
  const readyState: Extract<ConnectAppModuleState, { status: 'ready' }> = {
    ...state,
    status: 'ready',
    memberships,
    selectedOrganizationId: memberships[0]?.organization.id ?? null,
    authorization: { status: 'idle' }
  }

  return maybeAutoAuthorize(readyState)
}

function maybeAutoAuthorize(
  state: Extract<ConnectAppModuleState, { status: 'ready' }>
): UpdateResult<ConnectAppModuleState, ConnectAppMessage> {
  if (!state.route.app.autoAuthorize) {
    return state
  }

  const eligibleMembership = state.memberships.find((membership) =>
    state.route.app.allowedOrganizationRoles.includes(membership.role)
  )
  if (!eligibleMembership) {
    return state
  }

  return startAuthorization({
    ...state,
    selectedOrganizationId: eligibleMembership.organization.id
  })
}

function selectOrganization(
  state: Extract<ConnectAppModuleState, { status: 'ready' }>,
  organizationId: string
): ConnectAppModuleState {
  if (!state.memberships.some((membership) => membership.organization.id == organizationId)) {
    throw new Error(`Cannot select organization outside the loaded memberships: ${organizationId}`)
  }

  return {
    ...state,
    selectedOrganizationId: organizationId,
    authorization: { status: 'idle' }
  }
}

function startAuthorization(
  state: Extract<ConnectAppModuleState, { status: 'ready' }>
): UpdateResult<ConnectAppModuleState, ConnectAppMessage> {
  const membership = selectedEligibleMembership(state)
  return [
    { ...state, authorization: { status: 'authorizing' } },
    authorizeAndRedirect(state.route.request, membership.organization.id)
  ]
}

function selectedEligibleMembership(
  state: Extract<ConnectAppModuleState, { status: 'ready' }>
): OrganizationMembership {
  const membership = state.memberships.find(
    (membership) => membership.organization.id == state.selectedOrganizationId
  )
  if (!membership || !state.route.app.allowedOrganizationRoles.includes(membership.role)) {
    throw new Error('Cannot authorize an app without an eligible selected organization')
  }
  return membership
}

function authorizeAndRedirect(request: ConnectRequest, organizationId: string) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const command = match({
      codeChallenge: request.codeChallenge,
      codeChallengeMethod: request.codeChallengeMethod
    })
      .with({ codeChallenge: P.string, codeChallengeMethod: P.string }, (pkce) => ({
        type: 'accounts/authorizeApp' as const,
        clientId: request.clientId,
        state: request.oauthState,
        redirectUri: request.redirectUri,
        organizationId,
        scopes: parseScopes(request.scope),
        ...pkce
      }))
      .otherwise(() => ({
        type: 'accounts/authorizeApp' as const,
        clientId: request.clientId,
        state: request.oauthState,
        redirectUri: request.redirectUri,
        organizationId,
        scopes: parseScopes(request.scope)
      }))
    const result = yield* runSdkRequest(sdk.send(command))

    if (!result.ok) {
      return { type: 'authorizationFailed' } as const
    }

    const routing = yield* Routing
    yield* routing.redirectTo(result.response.redirectTo)
    return { type: 'authorizationCompleted' } as const
  })
}

function parseScopes(scope: string): string[] {
  if (!scope) {
    return []
  }
  return scope.split(' ')
}

function finishAuthorization(
  state: Extract<ConnectAppModuleState, { status: 'ready' }>,
  route: ConnectRoute,
  currentUserId: string | null
): ConnectAppModuleState {
  return {
    status: 'finishing',
    route,
    currentUserId,
    owner: {
      userId: state.userId,
      route: state.route,
      memberships: state.memberships,
      selectedOrganizationId: state.selectedOrganizationId
    },
    authPage: state.authPage,
    signUp: state.signUp
  }
}

function resumeLatestContext(
  state: Extract<ConnectAppModuleState, { status: 'finishing' }>,
  authorizationStatus: 'idle' | 'failed'
): UpdateResult<ConnectAppModuleState, ConnectAppMessage> {
  const ownedState: Extract<ConnectAppModuleState, { status: 'ready' }> = {
    status: 'ready',
    ...state.owner,
    authorization: { status: authorizationStatus },
    authPage: state.authPage,
    signUp: state.signUp
  }
  return Update.pipe(updateCurrentUser(ownedState, state.currentUserId), (currentState) =>
    updateRoute(currentState, state.route)
  )
}

function sameConnectRoute(left: ConnectRoute, right: ConnectRoute): boolean {
  if (left.status != right.status) {
    return false
  }
  if (left.status == 'inactive' && right.status == 'inactive') {
    return true
  }
  if (left.status == 'invalid' && right.status == 'invalid') {
    return left.reason == right.reason && left.clientId == right.clientId
  }
  if (left.status == 'active' && right.status == 'active') {
    return (
      left.request.clientId == right.request.clientId &&
      left.request.oauthState == right.request.oauthState &&
      left.request.redirectUri == right.request.redirectUri &&
      left.request.scope == right.request.scope &&
      left.request.codeChallenge == right.request.codeChallenge &&
      left.request.codeChallengeMethod == right.request.codeChallengeMethod
    )
  }
  return false
}

function unexpectedMessage(state: ConnectAppModuleState, message: ConnectAppMessage): never {
  throw new Error(`Unexpected connect-app message ${message.type} while ${state.status}`)
}
